import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { getAppBySlug } from '../data/appsData';

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl font-bold mb-4 text-white border-b border-white/10 pb-3">{title}</h2>
    <div className="text-gray-400 space-y-3 leading-relaxed">{children}</div>
  </section>
);

const AppTermsPage = () => {
  const { appSlug } = useParams();
  const app = getAppBySlug(appSlug);

  if (!app) return <Navigate to="/legal" replace />;

  const updated = 'June 28, 2025';
  const effective = 'June 28, 2025';

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Header */}
      <div
        className="py-14 relative"
        style={{ background: `linear-gradient(135deg, ${app.accentColor}22 0%, transparent 70%)` }}
      >
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/legal" className="text-sm text-gray-500 hover:text-white mb-6 inline-flex items-center gap-2 transition-colors">
            ← Back to Legal Hub
          </Link>
          <div className="flex items-center gap-4 mt-4">
            <span className="text-5xl">{app.icon}</span>
            <div>
              <p className="text-xs font-bold tracking-widest uppercase mb-1" style={{ color: app.accentColor }}>
                Terms of Service
              </p>
              <h1 className="text-3xl md:text-4xl font-bold text-white">{app.name}</h1>
              <p className="text-gray-500 text-sm mt-1">{app.packageName} · {app.category}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 mt-6 text-xs text-gray-500">
            <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10">Effective: {effective}</span>
            <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10">Last updated: {updated}</span>
            <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10">Platform: Google Play (Android)</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Intro */}
        <div
          className="p-6 rounded-2xl mb-10 border"
          style={{ background: `${app.accentColor}10`, borderColor: `${app.accentColor}30` }}
        >
          <p className="text-gray-300 leading-relaxed">
            Please read these Terms of Service ("Terms") carefully before using the{' '}
            <strong className="text-white">{app.name}</strong> application operated by{' '}
            <strong className="text-white">Riven Byte Studios</strong> ("we", "us", "our"). By downloading, installing,
            or using the App, you agree to be bound by these Terms. If you disagree with any part, do not use the App.
          </p>
        </div>

        <Section title="1. Acceptance of Terms">
          <p>
            By accessing or using <strong className="text-white">{app.name}</strong>, you confirm that you are at least
            13 years of age (or the applicable age of digital consent in your jurisdiction), and that you have the
            legal authority to enter into these Terms, either on behalf of yourself or the organization you represent.
          </p>
        </Section>

        <Section title="2. License to Use">
          <p>
            Riven Byte Studios grants you a limited, non-exclusive, non-transferable, revocable license to install and
            use the App on a device that you own or control, solely for your personal, non-commercial purposes, in
            accordance with these Terms.
          </p>
          <p>You may not:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Copy, modify, or distribute the App or its content</li>
            <li>Reverse engineer, decompile, or disassemble the App</li>
            <li>Remove or alter any proprietary notices or branding</li>
            <li>Use the App for any illegal or unauthorized purpose</li>
            <li>Attempt to gain unauthorized access to any part of the App or its infrastructure</li>
          </ul>
        </Section>

        <Section title="3. Intellectual Property">
          <p>
            The App and all its original content, features, and functionality are and will remain the exclusive property
            of Riven Byte Studios and its licensors. Our trademarks, logos, and service marks may not be used in
            connection with any product or service without prior written consent.
          </p>
        </Section>

        {app.usesAds && (
          <Section title="4. Advertising">
            <p>
              The App displays advertisements provided by <strong className="text-white">Google AdMob</strong> and other
              advertising partners. These advertisements help us provide the App free of charge. By using the App, you
              consent to the display of advertisements. You may not use any means to block, filter, or interfere with ads
              displayed within the App.
            </p>
          </Section>
        )}

        <Section title={`${app.usesAds ? '5' : '4'}. Disclaimer of Warranties`}>
          <p>
            The App is provided "as is" and "as available" without warranties of any kind, either express or implied,
            including but not limited to implied warranties of merchantability, fitness for a particular purpose, or
            non-infringement. We do not warrant that the App will be uninterrupted, error-free, or free of viruses or
            other harmful components.
          </p>
        </Section>

        <Section title={`${app.usesAds ? '6' : '5'}. Limitation of Liability`}>
          <p>
            To the maximum extent permitted by applicable law, Riven Byte Studios shall not be liable for any indirect,
            incidental, special, consequential, or punitive damages, including loss of profits, data, or goodwill,
            arising out of or related to your use of the App, even if we have been advised of the possibility of such
            damages.
          </p>
          <p className="mt-3">
            Our total cumulative liability to you for any claim arising out of or relating to these Terms or the App
            shall not exceed the greater of (a) the amount you paid for the App in the past twelve (12) months, or
            (b) USD $5.00.
          </p>
        </Section>

        <Section title={`${app.usesAds ? '7' : '6'}. User Conduct`}>
          <p>You agree not to use the App to:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Violate any applicable laws or regulations</li>
            <li>Impersonate any person or entity</li>
            <li>Engage in any conduct that restricts or inhibits others' use of the App</li>
            <li>Attempt to cheat, hack, or exploit game mechanics or advertising systems</li>
            <li>Transmit any unsolicited advertising or spam</li>
          </ul>
        </Section>

        <Section title={`${app.usesAds ? '8' : '7'}. Termination`}>
          <p>
            We reserve the right to terminate or suspend your access to the App immediately, without prior notice or
            liability, for any reason, including if you breach these Terms. Upon termination, your right to use the
            App will immediately cease.
          </p>
        </Section>

        <Section title={`${app.usesAds ? '9' : '8'}. Governing Law`}>
          <p>
            These Terms shall be governed and construed in accordance with the laws of the United Republic of Tanzania,
            without regard to its conflict of law provisions. Any disputes arising under these Terms shall be subject to
            the exclusive jurisdiction of the courts of Dar es Salaam, Tanzania.
          </p>
        </Section>

        <Section title={`${app.usesAds ? '10' : '9'}. Changes to Terms`}>
          <p>
            We reserve the right to modify these Terms at any time. We will notify users of material changes by updating
            the "Last updated" date. Your continued use of the App after any changes constitutes your acceptance of the
            new Terms. We encourage you to review these Terms periodically.
          </p>
        </Section>

        <Section title={`${app.usesAds ? '11' : '10'}. Google Play Additional Terms`}>
          <p>
            If you downloaded the App from the Google Play Store, you acknowledge that these Terms are between you and
            Riven Byte Studios only, and not with Google LLC. Google is not responsible for the App or its content.
            These Terms are in addition to Google Play's terms of service.
          </p>
          <p className="mt-3">
            Google Play Terms of Service:{' '}
            <a
              href="https://play.google.com/intl/en_us/about/play-terms/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              style={{ color: app.accentColor }}
            >
              https://play.google.com/intl/en_us/about/play-terms/
            </a>
          </p>
        </Section>

        <Section title={`${app.usesAds ? '12' : '11'}. Contact`}>
          <p>Questions about these Terms? Reach us at:</p>
          <div className="mt-4 p-5 rounded-xl bg-white/5 border border-white/10 space-y-2">
            <p><span className="text-gray-500">Studio:</span> <span className="text-white">Riven Byte Studios</span></p>
            <p><span className="text-gray-500">Email:</span>{' '}
              <a href={`mailto:${app.contact}`} className="underline" style={{ color: app.accentColor }}>
                {app.contact}
              </a>
            </p>
            <p><span className="text-gray-500">Website:</span>{' '}
              <a href="https://riven-byte.pages.dev" className="underline" style={{ color: app.accentColor }}>
                riven-byte.pages.dev
              </a>
            </p>
            <p><span className="text-gray-500">Location:</span> <span className="text-gray-300">Dar es Salaam, Tanzania</span></p>
          </div>
        </Section>

        {/* Nav to Privacy */}
        <div className="mt-12 p-6 rounded-2xl bg-white/4 border border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-gray-400 text-sm">Also read our Privacy Policy for this app</p>
            <p className="text-xs text-gray-600 mt-0.5">Explains how we handle your data</p>
          </div>
          <Link
            to={app.customPrivacyPath || `/apps/${app.slug}/privacy`}
            className="px-5 py-2.5 rounded-xl font-medium text-sm text-black flex-shrink-0"
            style={{ background: app.accentColor }}
          >
            Privacy Policy →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AppTermsPage;
